import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

import {
  anotarTarea,
  asignarPendiente,
  borrarTarea,
  cerrarTareaDada,
  choferesDeAvisos,
  crearTarea,
  editarTarea,
  genteParaCompartir,
  marcarTarea,
  marcarTerminada,
  miMail,
  misTareas,
  retirarTareaDada,
  tareasQueDi,
} from "../services/espacio";
import { traerAreas } from "../services/tickets";
import BotonConfirmar from "../components/funcionales/BotonConfirmar";
import FotoPrivada from "../components/funcionales/FotoPrivada";
import WhatsappChofer from "../components/funcionales/WhatsappChofer";
import { buscarFila, useItemPedido } from "../hooks/useItemPedido";
import { diaAr } from "../shared/fechaAr";
import {
  dadaPorOtro,
  desdeCuando,
  esperanMiOk,
  esSoloQuienLaDio,
  estadoDada,
  HECHA,
  LISTA,
  nombreDe,
  ordenarDadas,
  PENDIENTE,
} from "../shared/tareaDada";
import { derivadaDe } from "../shared/tareaDerivada";
import { esChofer, puedeVerTelefono } from "../shared/telefono";
import Loading from "./Loading";

import "./css/espacio.css";

/* Mis pendientes: la ticketera personal de cada uno (pedido de Nico, #273).
 *
 * "Un listado de lo que tengo que hacer": se anota una tarea, se tilda cuando
 * está lista, y listo. Es tuyo (RLS por dueño) — nadie más ve tus pendientes.
 * Cada tarea puede llevar una "Anotación" a mano (Nico, 28/09), aparte del
 * texto. Se guarda solo, apenas tocás algo.
 *
 * Las que te DERIVARON desde Avisos de ruta (Nico, 06/10: «si lo derivo a
 * Andy, que aparezca en la ticketera de Andy») llegan solas, las deja el bot,
 * y se ven distintas: quién te la pasó, «ver foto» en el mismo visor de las
 * otras fotos del hub, y el link a la novedad. Son tuyas igual: se tildan,
 * se anotan y se borran como cualquiera — y quien la derivó ve en Avisos de
 * ruta cuándo la tildaste. Ver `shared/tareaDerivada`.
 *
 * Y SE ABRE EN LA TAREA EXACTA (Igna, 06/10): al que le derivan algo le llega
 * un WhatsApp con `/mi-espacio/pendientes?ver=<id>`. Esa tarea queda a la
 * vista y titila; si ya está tildada, se abren las hechas; si no está (la
 * borró), se dice en vez de dejar la lista callada.
 *
 * Y EL NÚMERO DEL CHOFER (Nico, 09/10: «en cualquiera que venga de parte de
 * un chofer […] que aparezca el número del chofer clickeable»). Una derivada
 * ES el aviso de un chofer, así que al lado de «ver el aviso» va su WhatsApp
 * —el número desde el que escribió, que vive en el aviso—. Sólo con
 * `ver.telefono_chofer`; sin la llave ni se consulta.
 *
 * Y SE LE ANOTA A OTRO, PERSONAL O DEL SECTOR (Nico, #385/#387: «una
 * ticketera de taller y una personal de actividades, como comprar discos de
 * corte»). Al lado de «Agregar»: «Para» (vos, o alguien del hub) y
 * «personal / del sector». Personal va a los pendientes de esa persona —le
 * aparece con «Te la anotó Fulano»—; del sector abre un ticket en la
 * ticketera del área (la de su ficha, o la que se elija), asignado a ella.
 * Para vos y personal es lo de siempre (`crearTarea`). Lo demás pasa por
 * `asignarPendiente`, porque la RLS no deja escribir en los pendientes de
 * otro.
 *
 * Y LA QUE LE DOY A OTRO LA SIGO YO, Y SÓLO YO LA DOY POR TERMINADA (Nico,
 * 20/10: «le di una tarea a Ruben y quiero controlarla en el futuro. Y que
 * sólo yo la pueda dar por finalizada»). Es la misma fila, vista de los dos
 * lados (`shared/tareaDada`, migración 20261020210100):
 *  · el que la recibe la tilda como siempre, pero tildar es «la terminé»: no
 *    va a las hechas, queda con «falta el OK de Fulano». No le cambia el
 *    texto (es lo que le pidieron) ni la borra; la nota sí, es suya.
 *  · quien la dio la ve abajo, en «Las que di»: para quién, desde cuándo, la
 *    nota y en qué está; la da por terminada (recién ahí queda hecha para los
 *    dos), la reabre o la retira. Las que esperan su OK van arriba, y un
 *    cartel lo dice apenas entra.
 * Las propias, las del sector y las derivadas de Avisos de ruta siguen como
 * estaban. La regla la sostiene la base, no esta pantalla: un tilde que se
 * cuele igual vuelve con `solo_quien_la_dio`. Sin la migración, no vienen
 * las columnas, «Las que di» no aparece y todo se ve como hoy.
 */

/* Lo que se le dice a la persona cuando la base rechaza algo: los códigos de
   la migración (`solo_quien_la_dio`, `no_la_diste`) no se muestran crudos. */
const motivo = (e, porDefecto) =>
  esSoloQuienLaDio(e)
    ? "Ésa te la anotó otra persona: la da por terminada quien te la anotó."
    : /no_la_diste/.test(String(e?.message || ""))
      ? "No la encuentro entre las que diste: puede que ya no esté. Recargá la página."
      : e?.message || porDefecto;

/* Una fila. Va como componente propio (no una función adentro del render) para
   que el textarea de la anotación NO pierda el foco en cada tecla: si se
   redefine en cada render, React lo remonta y se corta el tipeo. */
function FilaTarea({ t, chofer, permisos, onTildar, onTerminada, onGuardarTexto, onBorrar, onGuardarNota }) {
  const [editTexto, setEditTexto] = useState(false);
  const [borrTexto, setBorrTexto] = useState(t.texto || "");
  const [nota, setNota] = useState(t.nota || "");
  const [verNota, setVerNota] = useState(!!String(t.nota || "").trim());
  const der = derivadaDe(t);
  // Me la anotó otro: tildar es «la terminé», y la cierra él (Nico, 20/10).
  const dada = dadaPorOtro(t);
  const estado = dada ? estadoDada(t) : null;
  const quien = dada ? t.origen_por || nombreDe(t.dado_por) : "";
  const tildada = dada ? estado !== PENDIENTE : t.hecho;
  const etiquetaCheck = !dada
    ? t.hecho ? `Marcar "${t.texto}" como pendiente` : `Marcar "${t.texto}" como hecha`
    : estado === HECHA ? `"${t.texto}": ${quien} la dio por terminada`
      : estado === LISTA ? `Todavía no terminé "${t.texto}"` : `Marcar "${t.texto}" como terminada`;

  const guardarTexto = () => {
    setEditTexto(false);
    const v = borrTexto.trim();
    if (v && v !== t.texto) onGuardarTexto(t, v);
    else setBorrTexto(t.texto || "");
  };

  const guardarNota = () => {
    if ((nota || "") !== (t.nota || "")) onGuardarNota(t, nota);
    if (!nota.trim()) setVerNota(false);
  };

  return (
    <div
      className={`esp-tarea-wrap${t.hecho ? " hecha" : ""}${der ? " derivada" : ""}${dada ? ` dada ${estado}` : ""}`}
      data-ver={t.id}
    >
      <div className="esp-tarea">
        <input
          type="checkbox" className="esp-tarea-check" checked={tildada}
          aria-label={etiquetaCheck}
          // Ya cerrada por quien la dio: la reabre sólo él.
          disabled={dada && estado === HECHA}
          title={dada && estado === HECHA ? `La reabre ${quien}` : undefined}
          onChange={() => (dada ? onTerminada(t) : onTildar(t))}
        />
        {editTexto ? (
          <input
            className="esp-tarea-edit" autoFocus value={borrTexto} aria-label="Editar la tarea"
            onChange={(e) => setBorrTexto(e.target.value)}
            onBlur={guardarTexto}
            onKeyDown={(e) => {
              if (e.key === "Enter") guardarTexto();
              if (e.key === "Escape") { setBorrTexto(t.texto || ""); setEditTexto(false); }
            }}
          />
        ) : (
          <span
            className="esp-tarea-texto"
            // Lo que me anotó otro es lo que me pidió: el texto no se toca.
            onClick={() => { if (!t.hecho && !dada) { setBorrTexto(t.texto || ""); setEditTexto(true); } }}
            title={t.hecho || dada ? "" : "Tocá para editar"}
          >
            {t.texto}
          </span>
        )}
        {!verNota && (
          <button
            className="esp-tarea-nota-add" title="Agregar una anotación"
            aria-label={`Agregar anotación a "${t.texto}"`}
            onClick={() => setVerNota(true)}
          >
            + nota
          </button>
        )}
        {/* La ✕ PREGUNTA ANTES: una tarea borrada no vuelve, y está al lado
            de «+ nota». Mismo «¿Borrar? Sí / No» que las hojas de Mi espacio.
            La que me anotó otro no lleva ✕: la retira quien la dio. */}
        {!dada && (
          <BotonConfirmar
            className="esp-tarea-x" aria-label={`Borrar "${t.texto}"`} title="Borrar"
            claseSi="esp-hoja-si" claseNo="esp-hoja-no"
            pregunta="¿Borrar?" si="Sí, borrar"
            onConfirmar={() => onBorrar(t)}
          >
            ✕
          </BotonConfirmar>
        )}
      </div>

      {t.origen === "asignada" && (
        <div className="esp-tarea-origen">
          <span className="esp-tarea-origen-chip">
            {t.origen_por ? `Te la anotó ${t.origen_por}` : "Te la anotaron"}
          </span>
          {/* «Marcada», no «le avisaste»: no le sale ningún mensaje a quien
              la dio, lo ve cuando entra (el cartel de arriba). Si dijera
              «avisaste», el que la tilda se quedaría esperando sin contarle. */}
          {dada && (
            <span className="esp-tarea-regla" role="note">
              {estado === LISTA
                ? `Marcada como terminada: falta el OK de ${quien}, que lo ve cuando entra a Mis pendientes.`
                : estado === HECHA
                  ? `${quien} le dio el OK.`
                  : `Tildala cuando la termines: la da por terminada ${quien}.`}
            </span>
          )}
        </div>
      )}

      {der && (
        <div className="esp-tarea-origen">
          <span className="esp-tarea-origen-chip">
            {der.por ? `Derivada por ${der.por}` : "Derivada"} desde Avisos de ruta
          </span>
          {der.foto && <FotoPrivada link={der.foto} etiqueta="ver foto" titulo={t.texto} />}
          {der.aviso && (
            <a className="esp-tarea-origen-link" href={der.aviso}>ver el aviso</a>
          )}
          {/* Sólo si el aviso es de un chofer: el número de alguien del staff
              que le escribió al bot no es «el número del chofer». */}
          {chofer && esChofer(chofer.rol) && (
            <WhatsappChofer
              numero={chofer.numero} permisos={permisos} nombre={chofer.quien}
              etiqueta="WhatsApp del chofer:"
            />
          )}
        </div>
      )}

      {verNota && (
        <textarea
          className="esp-tarea-nota" value={nota}
          aria-label={`Anotaciones de "${t.texto}"`}
          placeholder="Anotaciones… (Enter guarda, Shift+Enter baja de renglón)"
          rows={2}
          autoFocus={!String(t.nota || "").trim()}
          onChange={(e) => setNota(e.target.value)}
          onBlur={guardarNota}
          onKeyDown={(e) => {
            // Enter guarda y cierra (como el resto del hub); Shift+Enter baja de
            // renglón para una nota de varias líneas.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              guardarNota();
              e.target.blur();
            }
            if (e.key === "Escape") { setNota(t.nota || ""); e.target.blur(); }
          }}
        />
      )}
      {/* Las Anotaciones de una que me anotó otro salen en «Las que di» de
          quien la dio: se dice ANTES de escribir, para no anotar ahí algo que
          era para uno solo. */}
      {verNota && dada && (
        <p className="esp-tarea-nota-quien">Lo que anotes acá lo ve {quien}.</p>
      )}
    </div>
  );
}

/* Una de «Las que di»: la tarea vive en los pendientes del otro, y acá se ve
   en qué está. Quien la dio no la edita ni le escribe la nota (son del que la
   tiene): la da por terminada, la reabre o la retira. */
function FilaDada({ t, nombre, onCerrar, onRetirar }) {
  const estado = estadoDada(t);
  const nota = String(t.nota || "").trim();
  return (
    <div className={`esp-tarea-wrap esp-dada ${estado}`} data-dada={t.id}>
      <div className="esp-tarea">
        <span className="esp-dada-punto" aria-hidden="true">{estado === HECHA ? "✓" : "●"}</span>
        <span className="esp-tarea-texto esp-dada-texto">{t.texto}</span>
        {estado !== HECHA && (
          <button
            type="button" className="esp-btn chico esp-dada-ok"
            aria-label={`Dar por terminada "${t.texto}"`}
            onClick={() => onCerrar(t, true)}
          >
            Dar por terminada
          </button>
        )}
        {/* Lo mismo de los dos lados (vuelve a quedar pendiente), pero con
            nombres distintos: lo que nadie cerró no se «reabre». */}
        {estado !== PENDIENTE && (
          <button
            type="button" className="esp-btn chico"
            aria-label={`${estado === LISTA ? "No está terminada" : "Reabrir"} "${t.texto}"`}
            title={estado === LISTA ? "Le vuelve a quedar pendiente, sin el «la terminé»" : "Vuelve a sus pendientes"}
            onClick={() => onCerrar(t, false)}
          >
            {estado === LISTA ? "No está terminada" : "Reabrir"}
          </button>
        )}
        <BotonConfirmar
          className="esp-tarea-x" aria-label={`Retirar "${t.texto}"`} title="Retirarla"
          claseSi="esp-hoja-si" claseNo="esp-hoja-no"
          pregunta="¿Retirarla?" si="Sí, retirar"
          onConfirmar={() => onRetirar(t)}
        >
          ✕
        </BotonConfirmar>
      </div>
      <div className="esp-tarea-origen">
        <span className="esp-tarea-origen-chip">Para {nombre}</span>
        <span>{desdeCuando(t.creado_en)}</span>
        <span className={`esp-dada-estado ${estado}`}>
          {estado === LISTA
            ? `${nombre} dice que la terminó: falta tu OK`
            : estado === HECHA
              ? `La diste por terminada${t.hecho_en ? ` el ${diaAr(t.hecho_en)}` : ""}`
              : "Pendiente"}
        </span>
      </div>
      {nota && <p className="esp-dada-nota">Anotaciones: {nota}</p>}
    </div>
  );
}

const MisPendientes = () => {
  const { permisos = null } = useOutletContext() || {};
  const verTel = puedeVerTelefono(permisos);
  const [tareas, setTareas] = useState(null);
  // El chofer de cada aviso del que salió una derivada: { [id]: { numero, quien, rol } }.
  const [choferes, setChoferes] = useState({});
  const [error, setError] = useState(null);
  const [nueva, setNueva] = useState("");
  // Para quién y dónde (#385/#387). `para` "" = para mí; `area` "" = la de su ficha.
  const [gente, setGente] = useState(null);
  const [para, setPara] = useState("");
  const [ambito, setAmbito] = useState("personal");
  const [areas, setAreas] = useState(null);
  const [area, setArea] = useState("");
  const [anotada, setAnotada] = useState(null);
  const [verHechas, setVerHechas] = useState(false);
  // «Las que di» (Nico, 20/10). null = la base todavía no lo tiene: no se muestra.
  const [dadas, setDadas] = useState(null);
  const [verTerminadas, setVerTerminadas] = useState(false);
  const { ver, resaltar, listo } = useItemPedido();
  const [avisoPedido, setAvisoPedido] = useState("");
  const pedida = useRef("");

  useEffect(() => {
    misTareas().then(setTareas).catch((e) => setError(e.message || "No se pudo leer"));
  }, []);

  // Las que le di a otros. Si no se pueden leer, la parte no está y lo mío
  // se ve igual: no es motivo para tapar la lista.
  useEffect(() => {
    let vigente = true;
    Promise.resolve()
      .then(() => tareasQueDi())
      .then((d) => vigente && setDadas(Array.isArray(d) ? d : null))
      .catch(() => vigente && setDadas(null));
    return () => {
      vigente = false;
    };
  }, []);

  // La gente del hub, para «Para». Si no se puede leer, el selector no está
  // y se anota para uno, como siempre.
  useEffect(() => {
    let vigente = true;
    Promise.resolve()
      .then(() => Promise.all([genteParaCompartir(), Promise.resolve(miMail()).catch(() => "")]))
      .then(([g, yo]) => vigente && setGente(Array.isArray(g) ? g.filter((x) => x.mail !== yo) : null))
      .catch(() => vigente && setGente(null));
    return () => {
      vigente = false;
    };
  }, []);

  // Las áreas de la ticketera, recién cuando se elige «del sector».
  useEffect(() => {
    if (ambito !== "sector" || areas !== null) return;
    let vigente = true;
    Promise.resolve()
      .then(() => traerAreas())
      .then((a) => vigente && setAreas(Array.isArray(a) ? a : []))
      .catch(() => vigente && setAreas([]));
    return () => {
      vigente = false;
    };
  }, [ambito, areas]);

  /* Los avisos de las derivadas, en UNA consulta y sólo con la llave. La
     clave son los ids ordenados: tildar o anotar cambia `tareas` pero no qué
     avisos hay, y eso no tiene que volver a preguntar. */
  const refs = useMemo(
    () => [...new Set((tareas || []).map((t) => derivadaDe(t)?.ref).filter(Boolean))].sort().join(","),
    [tareas],
  );
  useEffect(() => {
    if (!verTel || !refs) return;
    let vigente = true;
    choferesDeAvisos(refs.split(","))
      .then((m) => vigente && setChoferes(m || {}))
      .catch(() => vigente && setChoferes({}));
    return () => {
      vigente = false;
    };
  }, [verTel, refs]);

  // La tarea que pidió el link (`?ver=`): si está tildada, se abren las
  // hechas para que se vea; si no está, se dice.
  useEffect(() => {
    if (!ver || !tareas) return;
    const t = tareas.find((x) => String(x.id) === ver);
    if (!t) {
      setAvisoPedido("No encontré esa tarea: puede que ya la hayas borrado.");
      listo();
      return;
    }
    setAvisoPedido("");
    pedida.current = ver;
    if (t.hecho) setVerHechas(true);
  }, [ver, tareas, listo]);

  // Ya dibujada: titila, y el `?ver=` se va.
  useEffect(() => {
    if (!ver || pedida.current !== ver) return;
    if (resaltar(buscarFila(ver))) listo();
  }, [ver, tareas, verHechas, resaltar, listo]);

  const agregar = async () => {
    const texto = nueva.trim();
    if (!texto) return;
    setNueva("");
    setAnotada(null);
    if (!para && ambito === "personal") {
      try {
        const fila = await crearTarea(texto);
        setTareas((t) => [...(t || []), fila]);
      } catch (e) {
        setNueva(texto); // no se perdió lo escrito
        setError(e.message || "No se pudo guardar");
      }
      return;
    }
    try {
      const r = await asignarPendiente({ texto, para, ambito, area });
      const quien = (gente || []).find((g) => g.mail === para);
      setAnotada({ ...r, nombre: quien?.nombre || para, seguir: r.tipo === "tarea" && dadas !== null });
      setError(null);
      // Con «Las que di» andando, la que le acabo de dar ya va ahí.
      if (r.tipo === "tarea" && r.id && dadas !== null) {
        setDadas((d) => [
          ...(d || []),
          { id: r.id, dueno: r.para || para, texto, nota: "", hecho: false, hecho_en: null,
            creado_en: new Date().toISOString(), lista_en: null },
        ]);
      }
      // Un ticket que me abrí a mí no es una tarea de esta lista; una
      // personal para mí no pasa por acá.
    } catch (e) {
      setNueva(texto);
      setError(
        /sin_sector/.test(e?.message || "")
          ? "No tiene sector: elegí uno."
          : /sin_cuenta/.test(e?.message || "")
            ? "No tiene cuenta en el hub."
            : "No se pudo guardar",
      );
    }
  };

  const tildar = async (t) => {
    const hecho = !t.hecho;
    setTareas((xs) =>
      xs.map((x) => (x.id === t.id ? { ...x, hecho, hecho_en: hecho ? new Date().toISOString() : null } : x)),
    );
    try {
      await marcarTarea(t.id, hecho);
    } catch (e) {
      setTareas((xs) => xs.map((x) => (x.id === t.id ? { ...x, hecho: !hecho } : x))); // revertir
      setError(motivo(e, "No se pudo guardar"));
    }
  };

  // La que me anotó otro: «la terminé» / «todavía no». No la cierra.
  const terminada = async (t) => {
    const antes = t.lista_en || null;
    const ahora = antes ? null : new Date().toISOString();
    setTareas((xs) => xs.map((x) => (x.id === t.id ? { ...x, lista_en: ahora } : x)));
    try {
      await marcarTerminada(t.id, !!ahora);
    } catch (e) {
      setTareas((xs) => xs.map((x) => (x.id === t.id ? { ...x, lista_en: antes } : x)));
      setError(motivo(e, "No se pudo guardar"));
    }
  };

  // Quien la dio: le da el OK (cerrar) o la reabre.
  const cerrarDada = async (t, cerrar) => {
    const antes = t;
    const cambio = cerrar
      ? { hecho: true, hecho_en: t.hecho_en || new Date().toISOString() }
      : { hecho: false, hecho_en: null, lista_en: null };
    setDadas((ds) => (ds || []).map((x) => (x.id === t.id ? { ...x, ...cambio } : x)));
    try {
      await cerrarTareaDada(t.id, cerrar);
    } catch (e) {
      setDadas((ds) => (ds || []).map((x) => (x.id === t.id ? antes : x)));
      setError(motivo(e, "No se pudo guardar"));
    }
  };

  const retirar = async (t) => {
    setDadas((ds) => (ds || []).filter((x) => x.id !== t.id));
    try {
      await retirarTareaDada(t.id);
    } catch (e) {
      setDadas((ds) => [...(ds || []), t]);
      setError(motivo(e, "No se pudo retirar"));
    }
  };

  // Si la base no lo deja (una que me anotó otro, leída antes de la
  // migración), vuelve el texto de antes y se dice por qué, como al tildar.
  const guardarTexto = async (t, texto) => {
    setTareas((xs) => xs.map((x) => (x.id === t.id ? { ...x, texto } : x)));
    try {
      await editarTarea(t.id, texto);
    } catch (e) {
      setTareas((xs) => xs.map((x) => (x.id === t.id ? { ...x, texto: t.texto } : x)));
      setError(motivo(e, "No se pudo guardar"));
    }
  };

  const guardarNota = async (t, nota) => {
    setTareas((xs) => xs.map((x) => (x.id === t.id ? { ...x, nota } : x)));
    try {
      await anotarTarea(t.id, nota);
    } catch (e) {
      setError(e.message || "No se pudo guardar la anotación");
    }
  };

  const borrar = async (t) => {
    setTareas((xs) => xs.filter((x) => x.id !== t.id));
    try {
      await borrarTarea(t.id);
    } catch (e) {
      setTareas((xs) => [...xs, t]);
      setError(motivo(e, "No se pudo borrar"));
    }
  };

  if (error && tareas === null) return <div className="panel-aviso"><h3>No se pudo consultar</h3></div>;
  if (tareas === null) return <Loading />;

  const { abiertas: dadasAbiertas, terminadas: dadasTerminadas } = ordenarDadas(dadas);
  const faltaMiOk = esperanMiOk(dadas);
  const primeraLista = dadasAbiertas.find((t) => estadoDada(t) === LISTA);
  const filaDada = (t) => (
    <FilaDada
      key={t.id} t={t} nombre={nombreDe(t.dueno, gente)}
      onCerrar={cerrarDada} onRetirar={retirar}
    />
  );

  const pendientes = tareas.filter((t) => !t.hecho);
  const hechas = tareas.filter((t) => t.hecho).sort((a, b) => (b.hecho_en || "").localeCompare(a.hecho_en || ""));

  const fila = (t) => (
    <FilaTarea
      key={t.id} t={t}
      chofer={verTel ? choferes[derivadaDe(t)?.ref] : null} permisos={permisos}
      onTildar={tildar} onTerminada={terminada} onGuardarTexto={guardarTexto}
      onGuardarNota={guardarNota} onBorrar={borrar}
    />
  );

  return (
    <div className="docs">
      <div className="esp-acciones">
        <h2 style={{ margin: 0, flex: 1 }}>Mis pendientes</h2>
      </div>

      <div className="esp-nueva">
        <input
          className="esp-nueva-input" value={nueva} aria-label="Anotar una tarea"
          placeholder="Anotá algo que tengas que hacer…"
          onChange={(e) => setNueva(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") agregar(); }}
        />
        <button className="btn-primario" onClick={agregar} disabled={!nueva.trim()}>Agregar</button>
      </div>

      <div className="esp-nueva-para">
        {gente && (
          <label>
            Para{" "}
            <select value={para} aria-label="Para quién" onChange={(e) => setPara(e.target.value)}>
              <option value="">mí</option>
              {gente.map((g) => (
                <option key={g.mail} value={g.mail}>{g.nombre || g.mail}</option>
              ))}
            </select>
          </label>
        )}
        <label>
          <input
            type="radio" name="ambito" value="personal"
            checked={ambito === "personal"} onChange={() => setAmbito("personal")}
          />{" "}
          personal
        </label>
        <label>
          <input
            type="radio" name="ambito" value="sector"
            checked={ambito === "sector"} onChange={() => setAmbito("sector")}
          />{" "}
          del sector
        </label>
        {ambito === "sector" && (
          <select value={area} aria-label="Sector" onChange={(e) => setArea(e.target.value)}>
            <option value="">su sector</option>
            {(areas || []).map((a) => (
              <option key={a.clave} value={a.clave}>{a.etiqueta}</option>
            ))}
          </select>
        )}
      </div>

      {anotada && (
        <p className="panel-aviso suave" role="status">
          {anotada.tipo === "ticket" ? (
            <>
              <Link to={`/tickets?ver=${anotada.id}`}>Ticket #{anotada.id}</Link>
              {anotada.etiqueta ? ` en ${anotada.etiqueta}` : ""}
              {anotada.nombre ? `, asignado a ${anotada.nombre}` : ""}
            </>
          ) : (
            `Le quedó en sus pendientes a ${anotada.nombre}` +
            (anotada.seguir ? ". La seguís abajo, en «Las que di»: la das por terminada vos." : "")
          )}
        </p>
      )}

      {/* Le muestra a quien la dio que falta su visto bueno. Es lo único que
          se le dice: no sale ningún mensaje cuando el otro la tilda. */}
      {faltaMiOk > 0 && (
        <p className="panel-aviso esp-falta-ok" role="status">
          {faltaMiOk === 1
            ? `${nombreDe(primeraLista.dueno, gente)} dice que terminó «${primeraLista.texto}»: falta tu OK. Está abajo, en «Las que di».`
            : `${faltaMiOk} de las que diste esperan tu OK. Están abajo, en «Las que di».`}
        </p>
      )}

      {error && <p className="panel-aviso suave">{error}</p>}
      {avisoPedido && <p className="panel-aviso suave" role="status">{avisoPedido}</p>}

      {pendientes.length === 0 ? (
        <p className="panel-aviso suave">
          {hechas.length > 0
            ? "¡No te queda nada pendiente! 🎉"
            : "Todavía no anotaste nada. Escribí arriba y tocá Agregar."}
        </p>
      ) : (
        <div className="esp-tareas">
          {pendientes.map(fila)}
        </div>
      )}

      {hechas.length > 0 && (
        <>
          <button className="esp-btn" style={{ marginTop: 18 }} onClick={() => setVerHechas((v) => !v)}>
            {verHechas ? "Ocultar" : "Ver"} hechas ({hechas.length})
          </button>
          {verHechas && (
            <div className="esp-tareas" style={{ marginTop: 10 }}>
              {hechas.map(fila)}
            </div>
          )}
        </>
      )}

      {dadas && dadas.length > 0 && (
        <section className="esp-dadas" aria-label="Las que di">
          <h3>Las que di</h3>
          <p className="esp-dadas-bajada">
            Lo que le anotaste a otro. Cuando te dice que la terminó, la das por terminada vos.
          </p>
          {dadasAbiertas.length > 0 ? (
            <div className="esp-tareas">{dadasAbiertas.map(filaDada)}</div>
          ) : (
            <p className="panel-aviso suave">Todas las que diste están terminadas.</p>
          )}
          {dadasTerminadas.length > 0 && (
            <>
              <button className="esp-btn" style={{ marginTop: 12 }} onClick={() => setVerTerminadas((v) => !v)}>
                {verTerminadas ? "Ocultar" : "Ver"} terminadas ({dadasTerminadas.length})
              </button>
              {verTerminadas && (
                <div className="esp-tareas" style={{ marginTop: 10 }}>
                  {dadasTerminadas.map(filaDada)}
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default MisPendientes;
